
import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';

interface TechBackgroundProps {
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'light' | 'dark';
  glow?: 'blue' | 'red' | 'purple' | 'green' | 'none';
}

const TechBackground = ({ 
  children, 
  className = "", 
  variant = 'default',
  glow = 'none'
}: TechBackgroundProps) => {
  // Define background colors based on variant
  const getBgClasses = () => {
    switch(variant) {
      case 'light':
        return 'bg-[#011b3c]/80 backdrop-blur-md';
      case 'dark':
        return 'bg-[#001026]/90 backdrop-blur-md';
      default:
        return 'bg-[#001233]/80 backdrop-blur-md';
    }
  };

  // Define glow effect based on glow prop
  const getGlowElement = () => {
    switch(glow) {
      case 'blue':
        return (
          <motion.div 
            className="absolute -top-20 right-20 w-60 h-60 rounded-full bg-[#33C3F0]/20 blur-3xl"
            animate={{ 
              opacity: [0.1, 0.2, 0.1],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
        );
      case 'red':
        return (
          <motion.div 
            className="absolute -bottom-20 left-20 w-60 h-60 rounded-full bg-[#FF6B6B]/20 blur-3xl"
            animate={{ 
              opacity: [0.1, 0.3, 0.1],
              scale: [1, 1.2, 1],
            }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          />
        );
      case 'purple':
        return (
          <motion.div 
            className="absolute top-1/4 left-1/4 w-40 h-40 rounded-full bg-[#9b00ff]/15 blur-3xl"
            animate={{ 
              opacity: [0.1, 0.25, 0.1],
              scale: [1, 1.15, 1],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        );
      case 'green':
        return (
          <motion.div 
            className="absolute bottom-1/4 right-1/4 w-40 h-40 rounded-full bg-[#29BF12]/15 blur-3xl"
            animate={{ 
              opacity: [0.1, 0.2, 0.1],
              scale: [1, 1.1, 1],
            }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`relative overflow-hidden rounded-xl border border-white/10 shadow-xl ${getBgClasses()} ${className}`}>
      {/* Tech grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBoLTQweiIvPjxwYXRoIGQ9Ik0wIDBoMXY0MGgtMXpNMzkgMGgxdjQwaC0xek0wIDBoNDB2MWgtNDB6TTAgMzloNDB2MWgtNDB6IiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMDcpIi8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
      
      {/* Tech circle pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMSIgZmlsbD0iI2ZmZmZmZiIgZmlsbC1vcGFjaXR5PSIwLjA1Ii8+PC9zdmc+')] opacity-30"></div>
      
      {/* Dynamic glow effect */}
      {getGlowElement()}
      
      {/* Scanline effect */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJzY2FubGluZSIgeD0iMCIgeT0iMCIgd2lkdGg9IjEiIGhlaWdodD0iOCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjEiIGhlaWdodD0iNCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIvPjxyZWN0IHg9IjAiIHk9IjQiIHdpZHRoPSIxIiBoZWlnaHQ9IjQiIGZpbGw9InJnYmEoMCwwLDAsMC4wMSkiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHg9IjAiIHk9IjAiIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjc2NhbmxpbmUpIi8+PC9zdmc+')] opacity-10 pointer-events-none"></div>
      
      {/* Children with relative positioning */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default TechBackground;
